import React, { useCallback, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import ProfessionalOnboardingChecklist from "./ProfessionalOnboardingChecklist";
import useProfessionalOnboardingProgress from "../../hooks/useProfessionalOnboardingProgress";
import { getValidToken } from "../../utils/auth";
import {
  ONBOARDING_COMPLETED_EVENT,
  ONBOARDING_TOTAL_STEPS,
} from "../../utils/proOnboarding";
import { useActiveBusiness } from "../../hooks/useActiveBusiness";
import { useConfirmLeave } from "../UnsavedChangesGuard";
import { hasTeamModeAccess } from "../../utils/teamModeAccess";
import { canUseProfessionalFeatures } from "../../utils/roleCapabilities";
import { professionalRouteConfig, recruiterRouteConfig } from "../../routes/routeConfig";
import "./ProfessionalOnboardingProgressWidget.css";

const RING_RADIUS = 27;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const ProfessionalOnboardingProgressWidget = () => {
  const navigate = useNavigate();
  const confirmLeave = useConfirmLeave();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { activeBusinessId, business, membership } = useActiveBusiness();

  const auth = getValidToken();
  const user = auth?.payload ?? null;
  const role = user?.role ?? null;
  const userId = user?._id || user?.id || null;
  const userUsername = user?.username || null;

  const isAllowedRole = canUseProfessionalFeatures(role);
  const isProfessionalDashboardRoute = professionalRouteConfig.some(({ path }) =>
    Boolean(matchPath({ path, end: true }, location.pathname))
  );
  const isRecruiterDashboardRoute = recruiterRouteConfig.some(({ path }) =>
    Boolean(matchPath({ path, end: true }, location.pathname))
  );
  const isProviderDashboardRoute = isProfessionalDashboardRoute || isRecruiterDashboardRoute;
  const isOnboardingRoute = Boolean(
    matchPath({ path: "/onboarding", end: true }, location.pathname) ||
      matchPath({ path: "/onboarding/team", end: true }, location.pathname) ||
      matchPath({ path: "/onboarding/team-settings", end: true }, location.pathname) ||
      matchPath({ path: "/onboarding/business-details", end: true }, location.pathname)
  );
  const shouldLoadOnboarding = Boolean(
    auth?.token && isAllowedRole && isProviderDashboardRoute && !isOnboardingRoute
  );

  const onboardingState = useProfessionalOnboardingProgress({
    userId,
    userUsername,
    enabled: shouldLoadOnboarding,
    activeBusinessId,
    providerId: membership?.provider || userId,
    teamMode: hasTeamModeAccess(business),
    businessRole: membership?.role || null,
  });

  const progressMeta = onboardingState?.progressMeta || {};
  const completedCount = progressMeta?.completedCount || 0;
  const totalSteps = progressMeta?.totalSteps || ONBOARDING_TOTAL_STEPS;
  const progress = Number.isFinite(progressMeta?.progress) ? progressMeta.progress : 0;
  const isComplete = Boolean(progressMeta?.isComplete);
  const hasError = Boolean(onboardingState?.error);

  const clampedProgress = Math.min(100, Math.max(0, progress));
  const progressOffset =
    RING_CIRCUMFERENCE - (clampedProgress / 100) * RING_CIRCUMFERENCE;
  const isVisible = shouldLoadOnboarding && (onboardingState.loading || !isComplete);
  const progressLabel = onboardingState.loading
    ? "..."
    : `${completedCount}/${totalSteps}`;

  useEffect(() => {
    if (!isVisible) {
      setOpen(false);
    }
  }, [isVisible]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const handleNavigate = useCallback(
    (path, options) => {
      if (!path) {
        return;
      }
      // The badge floats over every dashboard page, including ones with edits
      // in progress.
      confirmLeave(() => {
        setOpen(false);
        navigate(path, options);
      }, { to: path });
    },
    [confirmLeave, navigate]
  );

  const handleCompletion = useCallback(() => {
    if (typeof window !== "undefined" && userId) {
      window.dispatchEvent(
        new CustomEvent(ONBOARDING_COMPLETED_EVENT, {
          detail: { userId },
        })
      );
    }
    setOpen(false);
  }, [userId]);

  const accessibilityLabel = useMemo(() => {
    if (onboardingState.loading) {
      return "Open onboarding checklist. Progress is loading.";
    }

    return `Open onboarding checklist. ${completedCount} of ${totalSteps} steps completed.`;
  }, [completedCount, onboardingState.loading, totalSteps]);

  const refreshOnboarding = onboardingState.refresh;

  const toggleOpen = useCallback(() => {
    setOpen((previouslyOpen) => {
      const nextOpen = !previouslyOpen;
      if (nextOpen && typeof refreshOnboarding === "function") {
        refreshOnboarding();
      }
      return nextOpen;
    });
  }, [refreshOnboarding]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`pro-onboarding-widget${open ? " is-open" : ""}`}>
      {open && <div className="pro-onboarding-widget__backdrop" onClick={() => setOpen(false)} />}

      {open && (
        <section
          id="professional-onboarding-widget-panel"
          className="pro-onboarding-widget__panel"
          aria-label="Onboarding checklist"
        >
          <div className="pro-onboarding-widget__panel-header">
            <p>Onboarding checklist</p>
            <button
              type="button"
              className="pro-onboarding-widget__panel-close"
              onClick={() => setOpen(false)}
              aria-label="Close onboarding checklist"
            >
              ×
            </button>
          </div>

          <ProfessionalOnboardingChecklist
            compact
            userId={userId}
            userUsername={userUsername}
            onNavigate={handleNavigate}
            onComplete={handleCompletion}
            onboardingState={onboardingState}
            teamMode={hasTeamModeAccess(business)}
            activeBusinessId={activeBusinessId}
            providerId={membership?.provider || userId}
            businessRole={membership?.role || null}
          />
        </section>
      )}

      <span className="pro-onboarding-widget__label" aria-hidden="true">
        Onboarding
      </span>
      <button
        type="button"
        className={`pro-onboarding-widget__trigger${hasError ? " has-error" : ""}`}
        onClick={toggleOpen}
        aria-expanded={open}
        aria-controls="professional-onboarding-widget-panel"
        aria-label={accessibilityLabel}
      >
        <span className="pro-onboarding-widget__ring" aria-hidden="true">
          <svg viewBox="0 0 60 60" focusable="false">
            <circle
              className="pro-onboarding-widget__ring-progress"
              cx="30"
              cy="30"
              r={RING_RADIUS}
              style={{
                strokeDasharray: RING_CIRCUMFERENCE,
                strokeDashoffset: progressOffset,
              }}
            />
          </svg>
          <span className="pro-onboarding-widget__value">{progressLabel}</span>
        </span>
      </button>
    </div>
  );
};

export default ProfessionalOnboardingProgressWidget;
